/** .tuMetrics {"tu1": 0.85, "tu2": 0.87, "tu3": 6, "notes": "Roadmaps state; create hydrates links + refreshes stores; partial update skips omitted associations", "tuVersion": "2.5"} * .help "Roadmap store: list, selection, calendar mode; updates project store after roadmap save so timeline shows linked projects." */
// src/store/roadmapStore.ts
import { create } from 'zustand';
import { Roadmap, Milestone } from '../types';
import useUserStore from './userStore';
import useFeedStore from './feedStore';
import useCardStore from './cardStore';
import useProjectStore from './projectStore';
import useSprintStore from './sprintStore';
import useRequirementStore from './requirementStore';
import usePlanStore from './planStore';
import useFollowStore from './followStore';
import { supabase } from '../lib/supabase';
import type { RealtimeChannel } from '@supabase/supabase-js';
import {
  mapRoadmapFromDB,
  mapRoadmapToDB,
  fetchRoadmaps,
  insertRoadmap,
  updateRoadmapById,
  deleteRoadmapById,
  type RoadmapAssociationSync,
} from '@/lib/supabase/queries/roadmap-queries';

interface RoadmapState {
  roadmaps: Roadmap[];
  currentRoadmapId: string | null; // Keep for single selection contexts if needed elsewhere
  isLoading: boolean;
  error: string | null;
  selectedTeamId: string | null;
  roadmapDisplayMode: 'list' | 'calendar'; // MODIFIED: Removed 'board'
  selectedRoadmapId: string | null; // Keep for single selection contexts if needed elsewhere
  selectedRoadmapIds: string[]; // NEW: For multi-selection in calendar view
  calendarDetailLevel: 'sprints' | 'tasks'; // NEW: For calendar view granularity
  currentMainTab: 'roadmaps' | 'sprints' | 'cards' | 'requirements';
  lastFetchedAt: number | null; // Timestamp of last fetch for staleness check
  realtimeChannel: RealtimeChannel | null;

  initializeRoadmaps: () => Promise<void>;
  refreshRoadmaps: () => Promise<void>;
  refreshRoadmapsIfStale: () => Promise<void>;
  setupRealtimeSubscription: () => Promise<void>;
  cleanupRealtimeSubscription: () => void;
  setCurrentRoadmap: (roadmapId: string) => void;
  setSelectedTeamId: (teamId: string | null) => void;
  setRoadmapDisplayMode: (mode: 'list' | 'calendar') => void; // MODIFIED: Removed 'board'
  setSelectedRoadmapId: (roadmapId: string) => void;
  setSelectedRoadmapIds: (ids: string[]) => void; // NEW: Action for multi-selection
  setCalendarDetailLevel: (level: 'sprints' | 'tasks') => void; // NEW: Action for calendar detail level
  setCurrentMainTab: (tab: 'roadmaps' | 'sprints' | 'cards' | 'requirements') => void;
  addRoadmap: (data: {
    name: string;
    description: string;
    sprints: string[];
    cardIds: string[];
    projectIds: string[];
    featureIds?: string[];
    planIds?: string[];
    milestones: Milestone[];
    teamId?: string | null;
  }) => Promise<void>;
  // July 2026: accepts a partial — the implementation merges with the existing
  // roadmap (`??` fallbacks), so callers no longer have to rebuild the full object.
  updateRoadmap: (roadmapId: string, data: Partial<{
    name: string;
    description: string;
    status: Roadmap['status'];
    sprints: string[];
    cardIds: string[];
    projectIds: string[];
    milestones: Milestone[];
    teamId: string | null;
  }>) => Promise<void>;
  deleteRoadmap: (roadmapId: string) => Promise<void>;
  duplicateRoadmap: (roadmapId: string) => Promise<void>;
  voteOnRoadmap: (roadmapId: string, userId: string, voteType: 'up' | 'down') => Promise<void>;
  getUserVote: (roadmapId: string, userId: string) => 'up' | 'down' | null;
}

const useRoadmapStore = create<RoadmapState>((set, get) => ({
  roadmaps: [],
  currentRoadmapId: null,
  isLoading: false,
  error: null,
  selectedTeamId: null,
  roadmapDisplayMode: (localStorage.getItem('roadmapDisplayMode') as 'list' | 'calendar') || 'list', // MODIFIED: Default to 'list'
  selectedRoadmapId: null,
  selectedRoadmapIds: [], // NEW: Initialize as empty array
  calendarDetailLevel: (localStorage.getItem('calendarDetailLevel') as 'sprints' | 'tasks') || 'sprints', // NEW: Default calendar detail level
  currentMainTab: (localStorage.getItem('currentMainTab') as 'roadmaps' | 'sprints' | 'cards' | 'requirements') || 'roadmaps',
  lastFetchedAt: null,
  realtimeChannel: null,

  initializeRoadmaps: async () => {
    set({ isLoading: true, error: null });
    try {
      const currentUser = useUserStore.getState().currentUser;

      if (!currentUser?.companyId) {
        console.warn('initializeRoadmaps: No current user companyId found. Skipping roadmap initialization.');
        set({ roadmaps: [], isLoading: false });
        return;
      }

      const roadmaps = await fetchRoadmaps(currentUser.companyId, null);

      const savedDisplayMode = localStorage.getItem('roadmapDisplayMode');
      const initialDisplayMode: 'list' | 'calendar' = // MODIFIED: Removed 'board'
        savedDisplayMode === 'list' || savedDisplayMode === 'calendar'
          ? (savedDisplayMode as any)
          : 'list'; // MODIFIED: Default to 'list'

      const savedSelectedRoadmapId = localStorage.getItem('selectedRoadmapId');
      const initialSelectedRoadmapId = roadmaps.find(r => r.id === savedSelectedRoadmapId)?.id || null;

      // NEW: Load selectedRoadmapIds from local storage or default to EMPTY ARRAY if none saved
      const savedSelectedRoadmapIds = localStorage.getItem('selectedRoadmapIds');
      const initialSelectedRoadmapIds = savedSelectedRoadmapIds ? JSON.parse(savedSelectedRoadmapIds) : []; // MODIFIED: Default to empty array

      // NEW: Load calendarDetailLevel from local storage
      const savedCalendarDetailLevel = localStorage.getItem('calendarDetailLevel');
      const initialCalendarDetailLevel: 'sprints' | 'tasks' =
        savedCalendarDetailLevel === 'sprints' || savedCalendarDetailLevel === 'tasks'
          ? savedCalendarDetailLevel
          : 'sprints';

      set({
        roadmaps,
        currentRoadmapId: roadmaps[0]?.id || null,
        isLoading: false,
        roadmapDisplayMode: initialDisplayMode,
        selectedRoadmapId: initialSelectedRoadmapId,
        selectedRoadmapIds: initialSelectedRoadmapIds, // NEW: Set initial selectedRoadmapIds
        calendarDetailLevel: initialCalendarDetailLevel, // NEW: Set initial calendarDetailLevel
        lastFetchedAt: Date.now(),
      });
    } catch (error: any) {
      console.error('Error loading roadmaps:', error);
      set({ error: `Failed to load roadmaps: ${error.message}`, isLoading: false });
    }
  },

  setCurrentRoadmap: (roadmapId) => set({ currentRoadmapId: roadmapId }),

  setSelectedTeamId: (teamId) => {
    set({ selectedTeamId: teamId });
    get().initializeRoadmaps();
  },

  refreshRoadmaps: async () => {
    // Refetch roadmaps without changing UI state (like selected roadmap)
    const currentUser = useUserStore.getState().currentUser;

    if (!currentUser?.companyId) {
      console.warn('refreshRoadmaps: No current user companyId found.');
      return;
    }

    const teamIdForQuery = null;
    const roadmaps = await fetchRoadmaps(currentUser.companyId, teamIdForQuery);

    set({ roadmaps, lastFetchedAt: Date.now() });
    console.log('RoadmapStore: Roadmaps refreshed from DB');
  },

  refreshRoadmapsIfStale: async () => {
    const { lastFetchedAt, isLoading, roadmaps } = get();
    const now = Date.now();
    const STALE_THRESHOLD = 30000; // 30 seconds

    // If no roadmaps loaded yet, initialize
    if (roadmaps.length === 0) {
      await get().initializeRoadmaps();
      return;
    }

    // Skip if already loading or data is fresh
    if (isLoading || (lastFetchedAt && now - lastFetchedAt < STALE_THRESHOLD)) {
      console.log('refreshRoadmapsIfStale: Data is fresh, skipping refresh');
      return;
    }

    console.log('refreshRoadmapsIfStale: Data is stale, refreshing...');
    await get().refreshRoadmaps();
  },

  /**
   * Setup realtime subscription for roadmaps
   */
  setupRealtimeSubscription: async () => {
    // Clean up existing subscription
    get().cleanupRealtimeSubscription();

    const currentUser = useUserStore.getState().currentUser;
    if (!currentUser?.companyId) {
      console.warn('setupRealtimeSubscription: No current user companyId found. Skipping realtime subscription setup.');
      return;
    }

    // Ensure roadmaps are loaded first
    const { roadmaps, isLoading } = get();
    if (roadmaps.length === 0 && !isLoading) {
      await get().initializeRoadmaps();
    }

    const channel = supabase
      .channel('roadmaps_channel')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'roadmaps',
          filter: `company_id=eq.${currentUser.companyId}`,
        },
        (payload) => {
          console.log('Real-time roadmap payload received:', payload);
          const { eventType, new: newRow, old: oldRow } = payload;

          switch (eventType) {
            case 'INSERT': {
              const newRoadmap = mapRoadmapFromDB(newRow as any);
              // Dedupe: streamStore's agent-object handler may have inserted it already
              set((state) =>
                state.roadmaps.some((r) => r.id === newRoadmap.id)
                  ? state
                  : { roadmaps: [newRoadmap, ...state.roadmaps] }
              );
              break;
            }

            case 'UPDATE': {
              const updatedRoadmap = mapRoadmapFromDB(newRow as any);
              set((state) => ({
                roadmaps: state.roadmaps.map((r) =>
                  r.id === updatedRoadmap.id ? updatedRoadmap : r
                ),
              }));
              break;
            }

            case 'DELETE': {
              set((state) => ({
                roadmaps: state.roadmaps.filter((r) => r.id !== oldRow.id),
                currentRoadmapId:
                  state.currentRoadmapId === oldRow.id ? null : state.currentRoadmapId,
                selectedRoadmapId:
                  state.selectedRoadmapId === oldRow.id ? null : state.selectedRoadmapId,
                selectedRoadmapIds: state.selectedRoadmapIds.filter((id) => id !== oldRow.id),
              }));
              break;
            }
          }
        }
      )
      .subscribe();

    set({ realtimeChannel: channel });
    console.log('Roadmaps realtime subscription established');
  },

  /**
   * Cleanup realtime subscription
   */
  cleanupRealtimeSubscription: () => {
    const { realtimeChannel } = get();
    if (realtimeChannel) {
      supabase.removeChannel(realtimeChannel);
      set({ realtimeChannel: null });
      console.log('Roadmaps realtime subscription cleaned up');
    }
  },

  setRoadmapDisplayMode: (mode) => {
    set({ roadmapDisplayMode: mode });
    localStorage.setItem('roadmapDisplayMode', mode);
  },

  setSelectedRoadmapId: (roadmapId) => {
    set({ selectedRoadmapId: roadmapId });
    if (roadmapId) localStorage.setItem('selectedRoadmapId', roadmapId);
    else localStorage.removeItem('selectedRoadmapId');
  },

  // NEW: Action to set multiple selected roadmap IDs
  setSelectedRoadmapIds: (ids) => {
    set({ selectedRoadmapIds: ids });
    localStorage.setItem('selectedRoadmapIds', JSON.stringify(ids)); // MODIFIED: Persist to localStorage
  },

  // NEW: Action to set calendar detail level
  setCalendarDetailLevel: (level) => {
    set({ calendarDetailLevel: level });
    localStorage.setItem('calendarDetailLevel', level);
  },

  setCurrentMainTab: (tab) => {
    set({ currentMainTab: tab });
    localStorage.setItem('currentMainTab', tab);
  },

  addRoadmap: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser) throw new Error('No authenticated user');
      if (!currentUser.companyId) throw new Error('Current user is not associated with a company.');

      const roadmapData = mapRoadmapToDB({
        name: data.name,
        description: data.description,
        status: 'draft',
        milestones: data.milestones,
        sentimentVotes: { up: 0, down: 0, user_votes: [] },
        teamId: data.teamId ?? currentUser.team ?? undefined,
        companyId: currentUser.companyId,
        createdById: currentUser.id,     // ✅ ensure creator is set
        requestedById: currentUser.id,   // ✅ ensure requester is set (RLS delegate)
      });

      const featureIds = data.featureIds || [];
      const planIds = data.planIds || [];

      const newRoadmap = await insertRoadmap(roadmapData, {
        sprintIds: data.sprints,
        cardIds: data.cardIds,
        projectIds: data.projectIds,
        featureIds,
        planIds,
      });

      const mappedRoadmap = {
        ...mapRoadmapFromDB(newRoadmap),
        sprints: data.sprints,
        cardIds: data.cardIds,
        projectIds: data.projectIds,
      };

      useFeedStore.getState().addEvent({
        type: 'roadmap_created',
        title: 'New Roadmap Created',
        description: `${currentUser.name} created roadmap "${data.name}"`,
        userId: currentUser.id,
        initiatorId: currentUser.id,
        roadmapId: mappedRoadmap.id,
      });

      set((state) => ({
        roadmaps: [mappedRoadmap, ...state.roadmaps],
        isLoading: false,
      }));

      // Await refreshes so list/detail UI sees all linked entities immediately
      const refreshPlans = currentUser.companyId
        ? usePlanStore.getState().fetchPlans(currentUser.companyId)
        : Promise.resolve();
      await Promise.all([
        useCardStore.getState().initializeCards(),
        useProjectStore.getState().refreshProjects(),
        useSprintStore.getState().refreshSprints(),
        useRequirementStore.getState().initializeRequirements(),
        refreshPlans,
      ]);
    } catch (error: any) {
      console.error('Error adding roadmap:', error);
      set({ error: `Failed to add roadmap: ${error.message}`, isLoading: false });
      throw error;
    }
  },

  updateRoadmap: async (roadmapId, data) => {
    set({ isLoading: true, error: null });
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser) throw new Error('No authenticated user');

      const oldRoadmap = get().roadmaps.find(r => r.id === roadmapId);
      if (!oldRoadmap) throw new Error('Roadmap not found');

      // Merge scalar fields for the roadmaps row; association arrays sync only when provided
      const mergedData = {
        name: data.name ?? oldRoadmap.name,
        description: data.description ?? oldRoadmap.description,
        status: data.status ?? oldRoadmap.status,
        teamId: data.teamId ?? oldRoadmap.teamId,
        sprints: data.sprints ?? oldRoadmap.sprints,
        milestones: data.milestones ?? oldRoadmap.milestones,
        cardIds: data.cardIds ?? (oldRoadmap.cardIds || []),
        projectIds: data.projectIds ?? (oldRoadmap.projectIds || []),
      };

      const changes: string[] = [];

      if (data.name !== undefined && data.name !== oldRoadmap.name) {
        changes.push(`name from "${oldRoadmap.name}" to "${data.name}"`);
      }
      if (data.description !== undefined && data.description !== oldRoadmap.description) {
        changes.push('description changed');
      }
      if (data.status !== undefined && data.status !== oldRoadmap.status) {
        changes.push(`status changed to ${data.status}`);
      }
      if (data.teamId !== undefined && data.teamId !== oldRoadmap.teamId) {
        changes.push('team changed');
      }

      // sprints diff (only if sprints were provided)
      if (data.sprints !== undefined) {
        const oldSprintsSet = new Set(oldRoadmap.sprints);
        const newSprintsSet = new Set(data.sprints);
        const addedSprints = [...newSprintsSet].filter(id => !oldSprintsSet.has(id));
        const removedSprints = [...oldSprintsSet].filter(id => !newSprintsSet.has(id));
        if (addedSprints.length) changes.push(`added ${addedSprints.length} sprint(s)`);
        if (removedSprints.length) changes.push(`removed ${removedSprints.length} sprint(s)`);
      }

      // cards diff (only if cardIds were provided)
      if (data.cardIds !== undefined) {
        const oldCardIds = oldRoadmap.cardIds || [];
        const newCardIds = data.cardIds || [];
        if (oldCardIds.length !== newCardIds.length || oldCardIds.some((id, i) => id !== newCardIds[i])) {
          changes.push('associated cards updated');
        }
      }

      // milestones diff (only if milestones were provided)
      if (data.milestones !== undefined) {
        const oldMilestonesMap = new Map(oldRoadmap.milestones.map(m => [m.id, m]));
        const newMilestonesMap = new Map(data.milestones.map(m => [m.id, m]));
        const addedMilestones = data.milestones.filter(m => !oldMilestonesMap.has(m.id));
        const removedMilestones = oldRoadmap.milestones.filter(m => !newMilestonesMap.has(m.id));
        const updatedMilestones = data.milestones.filter(m => {
          const prev = oldMilestonesMap.get(m.id);
          return prev && (prev.title !== m.title || prev.description !== m.description || prev.date !== m.date || prev.completed !== m.completed);
        });
        if (addedMilestones.length) changes.push(`added ${addedMilestones.length} milestone(s)`);
        if (removedMilestones.length) changes.push(`removed ${removedMilestones.length} milestone(s)`);
        if (updatedMilestones.length) changes.push(`updated ${updatedMilestones.length} milestone(s)`);
      }

      let eventDescription = `${currentUser.name} updated roadmap "${mergedData.name}"`;
      eventDescription += changes.length ? `: ${changes.join(', ')}.` : '.';

      const roadmapUpdates: Partial<Roadmap> = {
        name: mergedData.name,
        description: mergedData.description,
        status: mergedData.status,
        milestones: mergedData.milestones,
        sentimentVotes: oldRoadmap.sentimentVotes,
        teamId: mergedData.teamId,
        companyId: oldRoadmap.companyId,
        // Intentionally not changing createdById/requestedById here
      };

      const associations: RoadmapAssociationSync = {};
      if (data.sprints !== undefined) associations.sprintIds = data.sprints;
      if (data.cardIds !== undefined) associations.cardIds = data.cardIds;
      if (data.projectIds !== undefined) associations.projectIds = data.projectIds;

      const updatedRoadmapRow = await updateRoadmapById(
        roadmapId,
        roadmapUpdates,
        Object.keys(associations).length > 0 ? associations : undefined
      );

      // Refresh projects/sprints/cards when associations may have changed
      if (associations.projectIds !== undefined) {
        await useProjectStore.getState().refreshProjects();
      }
      if (associations.sprintIds !== undefined) {
        await useSprintStore.getState().refreshSprints();
      }
      if (associations.cardIds !== undefined) {
        await useCardStore.getState().initializeCards();
      }

      const mappedRoadmap = {
        ...mapRoadmapFromDB(updatedRoadmapRow),
        sprints: mergedData.sprints,
        cardIds: mergedData.cardIds,
        projectIds: mergedData.projectIds,
      };

      useFeedStore.getState().addEvent({
        type: 'roadmap_updated',
        title: 'Roadmap Updated',
        description: eventDescription,
        userId: currentUser.id,
        initiatorId: currentUser.id,
        roadmapId: mappedRoadmap.id,
      });

      try {
        await useFollowStore.getState().notifyFollowers(
          [
            { entityType: 'roadmap', entityId: mappedRoadmap.id },
            ...(mappedRoadmap.projectIds ?? []).map((id) => ({ entityType: 'project' as const, entityId: id })),
          ],
          [currentUser.id],
          {
            type: 'roadmap_updated',
            title: 'Roadmap updated',
            description: eventDescription,
            initiatorId: currentUser.id,
            roadmapId: mappedRoadmap.id,
          },
        );
      } catch (followErr) {
        console.warn('Follower notify on roadmap update failed (non-critical):', followErr);
      }

      set((state) => ({
        roadmaps: state.roadmaps.map((r) => (r.id === roadmapId ? mappedRoadmap : r)),
        isLoading: false,
      }));
    } catch (error: any) {
      console.error('Error updating roadmap:', error);
      set({ error: `Failed to update roadmap: ${error.message}`, isLoading: false });
    }
  },

  deleteRoadmap: async (roadmapId) => {
    set({ isLoading: true, error: null });
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser) throw new Error('No authenticated user');

      const roadmap = get().roadmaps.find(r => r.id === roadmapId);
      if (!roadmap) throw new Error('Roadmap not found');

      await deleteRoadmapById(roadmapId);

      useFeedStore.getState().addEvent({
        type: 'roadmap_deleted',
        title: 'Roadmap Deleted',
        description: `${currentUser.name} deleted roadmap "${roadmap.name}"`,
        userId: currentUser.id,
        initiatorId: currentUser.id,
        roadmapId: roadmap.id,
      });

      set((state) => ({
        roadmaps: state.roadmaps.filter((r) => r.id !== roadmapId),
        currentRoadmapId: state.currentRoadmapId === roadmapId ? null : state.currentRoadmapId,
        isLoading: false,
      }));
    } catch (error: any) {
      console.error('Error deleting roadmap:', error);
      set({ error: `Failed to delete roadmap: ${error.message}`, isLoading: false });
    }
  },

  duplicateRoadmap: async (roadmapId) => {
    set({ isLoading: true, error: null });
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser) throw new Error('No authenticated user');

      const roadmap = get().roadmaps.find(r => r.id === roadmapId);
      if (!roadmap) throw new Error('Roadmap not found');
      if (!currentUser.companyId) throw new Error('Current user is not associated with a company.');

      const roadmapData = mapRoadmapToDB({
        name: `Copy of ${roadmap.name}`,
        description: roadmap.description,
        status: 'draft',
        milestones: roadmap.milestones,
        sentimentVotes: { up: 0, down: 0, user_votes: [] },
        teamId: currentUser.team || undefined,
        companyId: currentUser.companyId,
        createdById: currentUser.id,     // ✅ ensure creator on duplicates
        requestedById: currentUser.id,   // ✅ ensure requester on duplicates
      });

      const newRoadmap = await insertRoadmap(roadmapData, {
        sprintIds: roadmap.sprints,
      });

      const mappedRoadmap = {
        ...mapRoadmapFromDB(newRoadmap),
        sprints: [...roadmap.sprints],
      };

      useFeedStore.getState().addEvent({
        type: 'roadmap_created',
        title: 'Roadmap Duplicated',
        description: `${currentUser.name} created a copy of roadmap "${roadmap.name}"`,
        userId: currentUser.id,
        initiatorId: currentUser.id,
        roadmapId: mappedRoadmap.id,
      });

      set((state) => ({
        roadmaps: [mappedRoadmap, ...state.roadmaps],
        isLoading: false,
      }));
    } catch (error: any) {
      console.error('Error duplicating roadmap:', error);
      set({ error: `Failed to duplicate roadmap: ${error.message}`, isLoading: false });
    }
  },

  voteOnRoadmap: async (roadmapId, userId, voteType) => {
    try {
      const roadmap = get().roadmaps.find(r => r.id === roadmapId);
      if (!roadmap) return;

      const newVotes = { ...roadmap.sentimentVotes };
      const userVotes = [...newVotes.user_votes];

      const existingVoteIndex = userVotes.findIndex(vote => vote.user_id === userId);
      const existingVote = existingVoteIndex >= 0 ? userVotes[existingVoteIndex] : null;

      if (existingVote) {
        if (existingVote.type === voteType) {
          newVotes[voteType]--;
          userVotes.splice(existingVoteIndex, 1);
        } else {
          newVotes[existingVote.type]--;
          newVotes[voteType]++;
          userVotes[existingVoteIndex] = { user_id: userId, type: voteType };
        }
      } else {
        newVotes[voteType]++;
        userVotes.push({ user_id: userId, type: voteType });
      }

      newVotes.user_votes = userVotes;

      set((state) => ({
        roadmaps: state.roadmaps.map((r) =>
          r.id === roadmapId ? { ...r, sentimentVotes: newVotes } : r
        ),
      }));

      // Votes only — do not sync associations (empty projectIds used to wipe links)
      const updatedRoadmapRow = await updateRoadmapById(roadmapId, {
        sentimentVotes: newVotes,
      });

      const updatedRoadmap = {
        ...mapRoadmapFromDB(updatedRoadmapRow),
        sprints: roadmap.sprints,
        cardIds: roadmap.cardIds,
        projectIds: roadmap.projectIds,
      };

      const currentUser = useUserStore.getState().currentUser;
      if (currentUser) {
        useFeedStore.getState().addEvent({
          type: 'roadmap_voted',
          title: 'Roadmap Voted',
          description: `${currentUser.name} voted on roadmap "${roadmap.name}"`,
          userId: currentUser.id,
          initiatorId: currentUser.id,
          roadmapId: updatedRoadmap.id,
        });
      }

      set((state) => ({
        roadmaps: state.roadmaps.map((r) =>
          r.id === roadmapId ? updatedRoadmap : r
        ),
      }));
    } catch (error: any) {
      console.error('Error voting on roadmap:', error);
      set({ error: `Failed to vote on roadmap: ${error.message}` });
    }
  },

  getUserVote: (roadmapId, userId) => {
    const roadmap = get().roadmaps.find(r => r.id === roadmapId);
    if (!roadmap) return null;
    const userVote = roadmap.sentimentVotes.user_votes.find(vote => vote.user_id === userId);
    return userVote ? userVote.type : null;
  },
}));

export default useRoadmapStore;
